import { useState } from "react";
import moment from "moment";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button, Modal } from "src/components";
import TextArea from "src/components/ui/text-area.component";
import { useToast } from "src/hooks/useToast";
import { resolveKamFeedbackIssue } from "src/services/invoice-api";

// KAM CN-ADVICE changes ========================================
// "Feedback from Ext" panel: the invoice line items a stockist has flagged as an issue with
// MedVol from the KAM CN Advice screen, rendered under the invoice card. Each line can be closed
// individually — MedVol usually then reverts the invoice and re-enters it, which is why the
// backend keys these rows on invoice + item + batch (+ MRP as tie-breaker) rather than on
// invoice_product_id, which a revert destroys.

export interface KamFeedbackIssue {
  kam_cn_advice_reason_dtl_id: number;
  invoice_id: number;
  invoice_product_id: number | null;
  kam_cn_advice_reason_name: string | null;
  item_code: string;
  item_description: string | null;
  batch_no: string;
  mrp: number | null;
  inv_mrp: number | null;
  quantity: number | null;
  free_qty: number | null;
  inv_rate: number | null;
  discount_perc: number | null;
  purchase_rate: number | null;
  remarks: string | null;
  status: string | null;
  raised_on: string | null;
  resolved_by_name: string | null;
  resolved_date: string | null;
  resolution_remarks: string | null;
}

const showValue = (value: any) =>
  value === null || value === undefined || value === "" ? "—" : String(value);

const Field = ({ label, value }: { label: string; value: any }) => (
  <div className="flex flex-col">
    <p className="text-[11px] uppercase tracking-wide text-[#92400E] font-semibold mb-0.5">{label}</p>
    <span className="text-sm text-[#303030] font-medium break-words">{showValue(value)}</span>
  </div>
);

const KamFeedbackIssuesPanel = ({
  issues,
  company_code,
  canEdit,
  onResolved,
}: {
  issues: KamFeedbackIssue[];
  company_code: string | number;
  canEdit: boolean;
  onResolved?: () => void;
}) => {
  const { toast } = useToast();
  const [activeIssue, setActiveIssue] = useState<KamFeedbackIssue | null>(null);
  const [resolutionRemarks, setResolutionRemarks] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  if (!issues || issues.length === 0) return null;

  const pendingCount = issues.filter((issue) => issue.status !== "completed").length;

  const closeModal = () => {
    setActiveIssue(null);
    setResolutionRemarks("");
  };

  const handleResolve = async () => {
    if (!activeIssue) return;
    setIsLoading(true);
    try {
      const trimmed = resolutionRemarks.trim();
      await resolveKamFeedbackIssue(
        {
          kam_cn_advice_reason_dtl_id: activeIssue.kam_cn_advice_reason_dtl_id,
          ...(trimmed ? { resolution_remarks: trimmed } : {}),
        },
        company_code,
      );
      toast({ description: `Issue marked as resolved for ${activeIssue.item_code}`, variant: "default" });
      closeModal();
      onResolved?.();
    } catch (err: any) {
      toast({
        description: err?.response?.data?.message ?? "Failed to mark the issue as resolved",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="mx-5 mb-5 rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] overflow-hidden">
        <div className="px-5 py-3 bg-[#FEE2E2] border-b border-[#FCA5A5] flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-[#7F1D1D]" aria-hidden="true" />
          <h3 className="text-[#7F1D1D] font-semibold text-base">Feedback from Stockist Portal</h3>
          <span className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-full bg-white text-[#7F1D1D] border border-[#FCA5A5]">
            {pendingCount} of {issues.length} line item{issues.length > 1 ? "s" : ""} pending
          </span>
        </div>

        <div className="px-5 py-4 space-y-3">
          {issues.map((issue) => {
            const isResolved = issue.status === "completed";
            return (
              <div
                key={issue.kam_cn_advice_reason_dtl_id}
                // The flagged line is tinted so it stands out against the rest of the invoice —
                // amber while open, green once MedVol has closed it.
                className={`rounded-lg border p-4 ${
                  isResolved ? "border-[#A7F3D0] bg-[#ECFDF5]" : "border-[#FCD34D] bg-[#FEF3C7]"
                }`}
              >
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="text-sm font-semibold text-[#1B1A1F]">
                    {issue.item_code}
                    {issue.item_description ? ` — ${issue.item_description}` : ""}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-white border border-[#E0E0E0] text-[#666666]">
                    Batch {showValue(issue.batch_no)}
                  </span>
                  {issue.kam_cn_advice_reason_name && (
                    <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-[#FDE68A] text-[#92400E] border border-[#FCD34D]">
                      {issue.kam_cn_advice_reason_name}
                    </span>
                  )}
                  {isResolved ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-[#D1FAE5] text-[#065F46] border border-[#A7F3D0]">
                      <CheckCircle2 className="w-3 h-3" aria-hidden="true" /> Resolved
                    </span>
                  ) : (
                    <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-[#FEE2E2] text-[#8E1F0B] border border-[#FCA5A5]">
                      Pending with MedVol
                    </span>
                  )}
                  {issue.invoice_product_id === null && (
                    // No live invoice line matches this row — usually because the invoice is
                    // mid-revert, or the fix changed the item code / batch number.
                    <span className="text-xs px-2 py-0.5 rounded-full bg-white border border-[#E0E0E0] text-[#8A8A8A]">
                      Line not currently on the invoice
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-3">
                  <Field label="MRP" value={issue.inv_mrp ?? issue.mrp} />
                  <Field label="Qty" value={issue.quantity} />
                  <Field label="Free Qty" value={issue.free_qty} />
                  <Field label="Inv. Rate" value={issue.inv_rate} />
                  <Field label="Discount %" value={issue.discount_perc} />
                  <Field label="Purchase Rate" value={issue.purchase_rate} />
                  <Field
                    label="Raised On"
                    value={issue.raised_on ? moment(issue.raised_on).format("DD MMM YYYY, hh:mm A") : null}
                  />
                </div>

                <div className="mt-3 pt-3 border-t border-dashed border-[#FCD34D]/60">
                  <Field label="Stockist Remarks" value={issue.remarks} />
                </div>

                {isResolved && (
                  <div className="mt-3 pt-3 border-t border-dashed border-[#A7F3D0] grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2">
                    <Field label="Resolved By" value={issue.resolved_by_name} />
                    <Field
                      label="Resolved On"
                      value={issue.resolved_date ? moment(issue.resolved_date).format("DD MMM YYYY, hh:mm A") : null}
                    />
                    <Field label="Resolution Remarks" value={issue.resolution_remarks} />
                  </div>
                )}

                {!isResolved && canEdit && (
                  <div className="flex justify-end mt-3">
                    <Button
                      className="text-white bg-[#8051FF]"
                      onClick={() => {
                        setResolutionRemarks("");
                        setActiveIssue(issue);
                      }}
                    >
                      Mark Resolved
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Modal isOpen={!!activeIssue} isHeader={false} onCloseModal={closeModal}>
        <div className="flex flex-col gap-y-3 w-[500px] mx-auto bg-white p-5 border border-gray-300 shadow-lg rounded-lg">
          <p className="text-base font-semibold text-[#1B1A1F]">Mark Issue as Resolved</p>
          <p className="text-sm text-[#666666]">
            This will close the issue raised by the stockist on{" "}
            <span className="font-semibold text-[#303030]">{activeIssue?.item_code}</span> (batch{" "}
            {activeIssue?.batch_no}). The stockist will see it as resolved and can no longer withdraw it.
          </p>

          <div>
            <p className="text-sm text-[#333] mb-1">Resolution Remarks (optional)</p>
            <TextArea
              className="h-20 py-2"
              placeholder="What was corrected?"
              value={resolutionRemarks}
              onChange={(e) => setResolutionRemarks(e.target.value.slice(0, 1000))}
            />
          </div>

          <div className="flex justify-end gap-x-2 mt-2">
            <Button disabled={isLoading} className="bg-[#f5f5f5]" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              isLoading={isLoading}
              loaderSize="small"
              disabled={isLoading}
              className="text-white bg-[#8051FF]"
              onClick={handleResolve}
            >
              Proceed
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default KamFeedbackIssuesPanel;
// KAM CN-ADVICE changes ========================================
